import { toast } from "@/components/common/Toast"
import type { PermissionKey } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useEffect, type ReactElement } from "react"
import { Navigate } from "react-router"

interface RequirePermissionProps {
  /** Catalog VIEW key gating this route, e.g. PERMISSIONS.KB_VIEW. */
  permission: PermissionKey
  children: ReactElement
}

/**
 * Route-level permission gate — the permission counterpart to FeatureGate.
 * Wrap a route element so members without the VIEW key are redirected home
 * before any child fetch fires (kills 403 spam on `GET /questionnaires` etc).
 *
 * On denial: fires ONE translated toast (dedupe id keeps it a single toast
 * even when the user chains several gated URLs quickly), then redirects home.
 * The toast persists across the react-router navigation because the toast
 * library holds it in memory, not the unmounting component.
 *
 * Kill-switch inherits from useUser().hasPermission — when TEAM_MANAGEMENT is
 * off for the org, everything passes and this is a no-op. Admins wildcard.
 */
export function RequirePermission({
  permission,
  children,
}: RequirePermissionProps) {
  const { hasPermission } = useUser()
  const { translate } = useTranslation()
  const allowed = hasPermission(permission)

  useEffect(() => {
    if (!allowed) {
      toast.error(translate("PERMISSION/ROUTE_DENIED"), {
        id: "permission-denied",
      })
    }
  }, [allowed, translate])

  return allowed ? children : <Navigate to="/" replace />
}
